// @flow

import React from 'react';
import { compose } from 'recompose';
import gql from 'graphql-tag';
import { css } from '@emotion/core';
import { Dialog, Button, ModalContext, Column, Text, Paragraph, InputField } from '@8base/boost';
import { Form, Field } from '@8base-react/forms';
import { graphql } from 'react-apollo';
import { TABLES_SCHEMA_QUERY } from '@8base-react/table-schema-provider';

import { withTranslation } from 'react-i18next';
import { Trans } from 'utils/translate';
import { TOAST_SUCCESS_MESSAGE } from 'common/constants/apolloOperationContextOptions';

const TABLE_DELETE_DIALOG_ID = 'TABLE_DELETE_DIALOG_ID';

const TABLE_DELETE_MUTATION = gql`
  mutation TableDelete($id: ID!) {
    tableDelete(data: {id: $id}) {
      success
    }
  }
`;

type TableDeleteDialogProps = {
  t: Function,
  deleteTable: Function,
};

class TableDeleteDialog extends React.Component<TableDeleteDialogProps> {
  static contextType = ModalContext;

  args: Object;

  onSubmit = async () => {
    const { id } = this.args;

    await this.props.deleteTable({ variables: { id }});

    window.trackEvent('Data Builder', 'Delete table');

    this.context.closeModal(TABLE_DELETE_DIALOG_ID);
  };

  onClose = () => {
    this.context.closeModal(TABLE_DELETE_DIALOG_ID);
  };

  validate = (value) => value === this.args.name ? null : this.props.t('validation.valueNoMatch');

  renderFormContent = ({ handleSubmit, invalid, submitting, pristine }) => {
    const { t } = this.props;
    const { name } = this.args;

    return (
      <form onSubmit={ handleSubmit }>
        { /* Hidden submit button for submit by Enter */ }
        <input type="submit" style={{ display: 'none' }} />
        <Dialog.Header title={ t('dataBuilder.tableDeleteDialog.title') } onClose={ this.onClose } />
        <Dialog.Body>
          <Column gap="lg">
            <Paragraph css={ css`width: 100%;` } align="center">
              <Trans i18nKey="dataBuilder.tableDeleteDialog.note">
                You are deleting the data table <Text weight="bold">{ name }</Text>.<br />
                Continuing this action will result in data loss.
              </Trans>
            </Paragraph>
            <Field
              name="name"
              type="text"
              placeholder="Type the table's name to delete it"
              validate={ this.validate }
              component={ InputField }
              { ...E2E.$props('app.dataBuilder.tableDeleteDialog.nameInput') }
              { ...PT.$props('tableDeleteDialog.nameInput') }
            />
          </Column>
        </Dialog.Body>
        <Dialog.Footer>
          <Button color="neutral" variant="outlined" disabled={ submitting } onClick={ this.onClose } { ...PT.$props('tableDeleteDialog.cancelBtn') }>Cancel</Button>
          <Button
            color="danger"
            type="submit"
            disabled={ invalid || pristine }
            loading={ submitting }
            { ...E2E.$props('app.dataBuilder.tableDeleteDialog.submitBtn') }
            { ...PT.$props('tableDeleteDialog.submitBtn') }
          >Delete
          </Button>
        </Dialog.Footer>
      </form>
    );
  };

  renderForm = ({ args }) => {
    this.args = args;

    return (
      <Form onSubmit={ this.onSubmit } >
        { this.renderFormContent }
      </Form>
    );
  };

  render() {
    return (
      <Dialog id={ TABLE_DELETE_DIALOG_ID } size="md" onClose={ this.onClose }>
        { this.renderForm }
      </Dialog>
    );
  }
}

TableDeleteDialog = compose(
  withTranslation(),
  graphql(TABLE_DELETE_MUTATION, {
    name: 'deleteTable',
    options: ({ t }) => ({
      refetchQueries: [
        { query: TABLES_SCHEMA_QUERY },
      ],
      context: {
        [TOAST_SUCCESS_MESSAGE]: t('dataBuilder.tableDeleteDialog.success'),
      },
    }),
  }),
)(TableDeleteDialog);

// $FlowFixMe
TableDeleteDialog.ID = TABLE_DELETE_DIALOG_ID;

export { TableDeleteDialog };
